import { useCallback, useEffect, useState } from 'react'
import { api, type Auswertung, type Auswertungsansicht } from '../api'
import { Karte } from '../komponenten/Karte'
import { Tipp } from '../komponenten/Hinweis'

const BREITE_NAME = {
  viertel: 'Viertel', halb: 'Halb', voll: 'Voll',
} as const

export function Auswertungen({ nachAnsichtAenderung }: {
  nachAnsichtAenderung?: () => void
}) {
  const [kacheln, setKacheln] = useState<Auswertung[]>([])
  const [ansichten, setAnsichten] = useState<Auswertungsansicht[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState<string | null>(null)

  const laden = useCallback(() => {
    setFehler(null)
    return Promise.all([api.auswertungen(), api.ansichten()])
      .then(([k, a]) => { setKacheln(k); setAnsichten(a) })
      .catch((e) => setFehler(e.message))
  }, [])
  useEffect(() => { void laden() }, [laden])

  const schalten = async (k: Auswertung) => {
    setLaeuft(k.schluessel); setFehler(null)
    try { await api.verwaltung.auswertungSetzen(k.schluessel, !k.aktiv); await laden() }
    catch (e) { setFehler(e instanceof Error ? e.message : String(e)) }
    finally { setLaeuft(null) }
  }

  const ansichtSchalten = async (a: Auswertungsansicht) => {
    setLaeuft(a.schluessel); setFehler(null)
    try {
      await api.verwaltung.ansichtSetzen(a.schluessel, !a.aktiv)
      await laden()
      nachAnsichtAenderung?.()
    } catch (e) { setFehler(e instanceof Error ? e.message : String(e)) }
    finally { setLaeuft(null) }
  }

  const breiteSetzen = async (k: Auswertung, breite: Auswertung['breite']) => {
    setLaeuft(k.schluessel); setFehler(null)
    try { await api.verwaltung.auswertungBreiteSetzen(k.schluessel, breite); await laden() }
    catch (e) { setFehler(e instanceof Error ? e.message : String(e)) }
    finally { setLaeuft(null) }
  }

  const bewegen = async (ansicht: string, index: number, richtung: -1 | 1) => {
    const liste = kacheln.filter((k) => k.ansicht === ansicht)
    const ziel = index + richtung
    if (ziel < 0 || ziel >= liste.length) return
    ;[liste[index], liste[ziel]] = [liste[ziel], liste[index]]
    setLaeuft(ansicht); setFehler(null)
    try {
      await api.verwaltung.auswertungenOrdnen(ansicht, liste.map((k) => k.schluessel))
      await laden()
    } catch (e) { setFehler(e instanceof Error ? e.message : String(e)) }
    finally { setLaeuft(null) }
  }

  const zuruecksetzen = async (ansicht: string) => {
    setLaeuft(ansicht); setFehler(null)
    try {
      await api.verwaltung.auswertungenZuruecksetzen(ansicht)
      await laden()
      nachAnsichtAenderung?.()
    }
    catch (e) { setFehler(e instanceof Error ? e.message : String(e)) }
    finally { setLaeuft(null) }
  }

  if ((!kacheln.length || !ansichten.length) && !fehler) {
    return <div className="laedt">Wird geladen …</div>
  }

  return (
    <div className="auswertungen-einrichten">
      {fehler && <div className="fehler" role="alert">{fehler}</div>}
      {ansichten.map((a) => {
        const liste = kacheln.filter((k) => k.ansicht === a.schluessel)
        const abweichend = a.abweichend || liste.some((k) => k.abweichend)
        return (
          <Karte key={a.schluessel} titel={a.name}
                 neben={a.beschreibung}
                 werkzeug={abweichend ? (
                   <div className="ansicht-werkzeuge">
                     <label>
                       <input type="checkbox" checked={a.aktiv}
                              disabled={laeuft !== null}
                              onChange={() => void ansichtSchalten(a)} />
                       Ansicht eingeschaltet
                     </label>
                     <button className="verweis" disabled={laeuft !== null}
                             onClick={() => void zuruecksetzen(a.schluessel)}>
                       Zurücksetzen
                     </button>
                   </div>
                 ) : (
                   <label className="ansicht-schalter">
                     <input type="checkbox" checked={a.aktiv}
                            disabled={laeuft !== null}
                            onChange={() => void ansichtSchalten(a)} />
                     Ansicht eingeschaltet
                   </label>
                 )}>
            <ol className={`auswertungsliste${a.aktiv ? '' : ' ansicht-inaktiv'}`}>
              {liste.map((k, index) => (
                <li key={k.schluessel} className={k.abweichend ? 'abweichend' : undefined}>
                  <label>
                    <input type="checkbox" checked={k.aktiv}
                           disabled={laeuft !== null}
                           onChange={() => void schalten(k)} />
                    <span><b>{k.name}</b><small>{k.beschreibung}</small></span>
                  </label>
                  {k.abweichend && <span className="merker">angepasst</span>}
                  {k.faehigkeiten.breite.length > 1 && (
                    <select aria-label={`Breite von ${k.name}`} value={k.breite}
                            disabled={laeuft !== null}
                            onChange={(e) => void breiteSetzen(
                              k, e.target.value as Auswertung['breite'])}>
                      {k.faehigkeiten.breite.map((breite) => (
                        <option key={breite} value={breite}>{BREITE_NAME[breite]}</option>
                      ))}
                    </select>
                  )}
                  <div className="reihenfolge" aria-label={`Reihenfolge von ${k.name}`}>
                    <Tipp text="Nach oben">
                      <button aria-label={`${k.name} nach oben`}
                              disabled={laeuft !== null || index === 0}
                              onClick={() => void bewegen(a.schluessel, index, -1)}>↑</button>
                    </Tipp>
                    <Tipp text="Nach unten">
                      <button aria-label={`${k.name} nach unten`}
                              disabled={laeuft !== null || index === liste.length - 1}
                              onClick={() => void bewegen(a.schluessel, index, 1)}>↓</button>
                    </Tipp>
                  </div>
                </li>
              ))}
            </ol>
          </Karte>
        )
      })}
    </div>
  )
}
